import React, { useMemo } from "react";
import { useComputedValues } from "@/contexts/ComputedValuesContext";
import { BuildPromptSteps } from "@/types/flows";
import { PromptLabelReviewCard } from "./Cards/PromptLabelReviewCard";
import { PromptLabelAnnotateCard } from "./Cards/PromptLabelAnnotateCard";
import { useModel } from "@/contexts/ModelContext";
import { useTextPromptMode } from "@/store";

export const PromptLabelCard = ({
    name,
    mode,
    onApprove,
    onReject,
    totalLabelsCount
}: {
    name: string;
    mode: BuildPromptSteps;
    onApprove?: (classFilter: string) => void;
    onReject?: (classFilter: string) => void;
    totalLabelsCount: number;
}) => {
    const textPromptMode = useTextPromptMode();
    const { currentFramePredictions, allAnnotationsAcrossFiles } = useComputedValues();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const { promptLabels } = useModel();

    const promptLabel = promptLabels.get(name);

    const { annotations, annotationsConfirmed, annotationsEmpty } = useMemo(() => {
        const annotationsForLabel = (
            Array.isArray(allAnnotationsAcrossFiles) ? allAnnotationsAcrossFiles : []
        )
            .filter((anno) => anno?.label === name)
            .sort((a, b) => {
                // Sort by modifiedAt timestamp (most recent first), then by id for stable sorting
                const aModifiedAt = (a as any).modifiedAt || 0;
                const bModifiedAt = (b as any).modifiedAt || 0;
                if (aModifiedAt !== bModifiedAt) {
                    return bModifiedAt - aModifiedAt; // Most recent first
                }
                return a.id.localeCompare(b.id); // Stable sort by id
            });

        return {
            annotations: annotationsForLabel,
            annotationsConfirmed: annotationsForLabel.filter((anno) => !anno.empty && !anno.draft),
            annotationsEmpty: annotationsForLabel.filter((anno) => anno.empty)
        };
    }, [allAnnotationsAcrossFiles, name]);

    // Check if there are predictions for this class
    const predictionsForThisLabel = useMemo(() => {
        return (Array.isArray(currentFramePredictions) ? currentFramePredictions : []).filter(
            (pred) => pred?.class === name
        );
    }, [currentFramePredictions, name]);

    const predictionCount = predictionsForThisLabel.length;
    const hasPredictions = predictionCount > 0;

    const visiblePredictionsForThisLabel = useMemo(() => {
        if (!promptLabel) return [];

        return predictionsForThisLabel.filter(
            (pred) => pred.confidence >= promptLabel.confidenceThreshold
        );
    }, [predictionsForThisLabel, promptLabel]);

    const visibleAnnotationsForThisLabel = useMemo(() => {
        if (!textPromptMode) return annotationsConfirmed;
        if (!promptLabel) return [];

        return annotationsConfirmed.filter(
            (anno) => !anno.confidence || anno.confidence >= promptLabel.confidenceThreshold
        );
    }, [annotationsConfirmed, promptLabel, textPromptMode]);

    // Render the appropriate child component based on mode
    if (mode === BuildPromptSteps.REVIEW_PREDICTIONS && hasPredictions) {
        return (
            <PromptLabelReviewCard
                inputRef={inputRef}
                name={name}
                // unique to ReviewCard
                onApprove={onApprove!}
                onReject={onReject!}
                initialConfidenceThreshold={promptLabel?.confidenceThreshold}
                predictionsForThisLabel={visiblePredictionsForThisLabel}
                totalLabelsCount={totalLabelsCount}
                mode={mode}
            />
        );
    }

    return (
        <PromptLabelAnnotateCard
            inputRef={inputRef}
            name={name}
            initialConfidenceThreshold={promptLabel?.confidenceThreshold}
            annotationsForThisLabel={visibleAnnotationsForThisLabel}
            allAnnotationsForThisLabel={annotationsConfirmed}
            allAnnotationsIncludingDrafts={annotations}
            initiallyEditing={!!annotationsEmpty.length}
        />
    );
};
